import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Beaker, Plus, Trash2, Save, RefreshCw, Edit2, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { labTestsAPI, qcAPI } from '../../services/api';

const LEVELS = [
  { value: 'normal', label: 'Normal' },
  { value: 'pathological', label: 'Pathological' },
  { value: 'level_3', label: 'Level 3' },
];

const emptyRow = () => ({
  lab_test_id: '',
  target_mean: '',
  target_sd: '',
});

const initialForm = {
  control_name: '',
  lot_number: '',
  level: 'normal',
  critical_rejection_threshold: '3sd',
  expiry_date: '',
  is_active: true,
  items: [emptyRow()],
};

function QCMaterialsSetup() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [controls, setControls] = useState([]);
  const [tests, setTests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [searchByRow, setSearchByRow] = useState({});

  const fetchPageData = async () => {
    setLoading(true);
    try {
      const [controlsRes, testsRes] = await Promise.all([
        qcAPI.getControls({ include_expired: true, limit: 100 }),
        labTestsAPI.getAll({ limit: 1000 }),
      ]);

      const controlsItems = controlsRes.data?.data?.items || [];
      const testItems = testsRes.data?.data?.items || [];
      setControls(Array.isArray(controlsItems) ? controlsItems : []);
      setTests(Array.isArray(testItems) ? testItems : []);
    } catch (error) {
      toast.error('تعذر تحميل بيانات الكنترولات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPageData();
  }, []);

  const filteredControls = useMemo(() => {
    if (!searchTerm.trim()) return controls;
    const q = searchTerm.toLowerCase();
    return controls.filter(
      (c) =>
        String(c.control_name || '').toLowerCase().includes(q) ||
        String(c.lot_number || '').toLowerCase().includes(q)
    );
  }, [controls, searchTerm]);

  const setRowValue = (index, patch) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], ...patch };
      return { ...prev, items };
    });
  };

  const addRow = () => setForm((prev) => ({ ...prev, items: [...prev.items, emptyRow()] }));

  const removeRow = (index) =>
    setForm((prev) => {
      const items = prev.items.filter((_, i) => i !== index);
      return { ...prev, items: items.length ? items : [emptyRow()] };
    });

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
    setSearchByRow({});
  };

  const onEdit = (control) => {
    setEditingId(control._id);
    setForm({
      control_name: control.control_name || '',
      lot_number: control.lot_number || '',
      level: control.level || 'normal',
      critical_rejection_threshold: control.critical_rejection_threshold || '3sd',
      expiry_date: control.expiry_date ? String(control.expiry_date).slice(0, 10) : '',
      is_active: control.is_active !== false,
      items:
        Array.isArray(control.items) && control.items.length
          ? control.items.map((item) => ({
              lab_test_id: item.lab_test_id || '',
              target_mean: item.target_mean ?? '',
              target_sd: item.target_sd ?? '',
            }))
          : [emptyRow()],
    });
  };

  const onDelete = async (id) => {
    if (!window.confirm('تأكيد حذف الكنترول؟')) return;
    try {
      await qcAPI.deleteControl(id);
      toast.success('تم حذف الكنترول');
      fetchPageData();
      if (editingId === id) resetForm();
    } catch (error) {
      toast.error(error.response?.data?.message || 'تعذر حذف الكنترول');
    }
  };

  const validateForm = () => {
    if (!form.control_name.trim()) return 'اسم الكنترول مطلوب';
    if (!form.lot_number.trim()) return 'رقم التشغيلة مطلوب';
    if (!form.critical_rejection_threshold) return 'حد الرفض الحرج مطلوب';
    if (!form.expiry_date) return 'تاريخ الصلاحية مطلوب';
    if (!Array.isArray(form.items) || !form.items.length) return 'أضف تحليل واحد على الأقل';

    const seen = new Set();
    for (let i = 0; i < form.items.length; i += 1) {
      const row = form.items[i];
      if (!row.lab_test_id) return `يرجى اختيار التحليل في الصف ${i + 1}`;
      if (seen.has(row.lab_test_id)) return 'لا يمكن تكرار نفس التحليل داخل الكنترول';
      seen.add(row.lab_test_id);
      if (row.target_mean === '' || Number.isNaN(Number(row.target_mean))) {
        return `المتوسط المستهدف غير صالح في الصف ${i + 1}`;
      }
      if (row.target_sd === '' || Number.isNaN(Number(row.target_sd)) || Number(row.target_sd) < 0) {
        return `الانحراف المعياري غير صالح في الصف ${i + 1}`;
      }
    }

    return null;
  };

  const onSave = async () => {
    const error = validateForm();
    if (error) {
      toast.error(error);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        items: form.items.map((row) => ({
          lab_test_id: row.lab_test_id,
          target_mean: Number(row.target_mean),
          target_sd: Number(row.target_sd),
        })),
      };

      if (editingId) {
        await qcAPI.updateControl(editingId, payload);
        toast.success('تم تحديث الكنترول بنجاح');
      } else {
        await qcAPI.createControl(payload);
        toast.success('تم حفظ الكنترول بنجاح');
      }

      resetForm();
      fetchPageData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'تعذر حفظ الكنترول');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#1E293B] flex items-center justify-center">
                <Beaker className="w-6 h-6 text-slate-100" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#1E293B]">إعداد الكنترولات</h1>
                <p className="text-sm text-slate-500 mt-1">تعريف مواد مراقبة الجودة وربطها بالتحاليل</p>
              </div>
            </div>
            <button
              onClick={fetchPageData}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 hover:bg-slate-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              تحديث
            </button>
          </div>
        </motion.div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">اسم الكنترول</label>
              <input
                value={form.control_name}
                onChange={(e) => setForm((p) => ({ ...p, control_name: e.target.value }))}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="مثال: PCCC1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">رقم التشغيلة</label>
              <input
                value={form.lot_number}
                onChange={(e) => setForm((p) => ({ ...p, lot_number: e.target.value }))}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="LOT-2026-01"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">المستوى</label>
              <select
                value={form.level}
                onChange={(e) => setForm((p) => ({ ...p, level: e.target.value }))}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl bg-white"
              >
                {LEVELS.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">حد الرفض الحرج</label>
              <select
                value={form.critical_rejection_threshold}
                onChange={(e) => setForm((p) => ({ ...p, critical_rejection_threshold: e.target.value }))}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl bg-white"
              >
                <option value="2sd">±2 SD</option>
                <option value="3sd">±3 SD</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">تاريخ الصلاحية</label>
              <input
                type="date"
                value={form.expiry_date}
                onChange={(e) => setForm((p) => ({ ...p, expiry_date: e.target.value }))}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl"
              />
            </div>
          </div>

          <div className="border border-slate-200 rounded-2xl overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700">تفاصيل التحاليل داخل الكنترول</h3>
              <button
                onClick={addRow}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700"
              >
                <Plus className="w-4 h-4" /> إضافة تحليل
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="border-b border-slate-200">
                  <tr className="text-right text-xs text-slate-500">
                    <th className="px-4 py-3">اسم التحليل</th>
                    <th className="px-4 py-3">المتوسط المستهدف</th>
                    <th className="px-4 py-3">الانحراف المعياري (SD)</th>
                    <th className="px-4 py-3 w-20">حذف</th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((row, index) => {
                    const rowQuery = (searchByRow[index] || '').trim().toLowerCase();
                    const testsForRow = tests.filter((test) => {
                      if (!rowQuery) return true;
                      return (
                        String(test.name_ar || '').toLowerCase().includes(rowQuery) ||
                        String(test.name_en || '').toLowerCase().includes(rowQuery)
                      );
                    });
                    return (
                      <tr key={`row-${index}`} className="border-b border-slate-100 last:border-b-0">
                        <td className="px-4 py-3">
                          <input
                            value={searchByRow[index] || ''}
                            onChange={(e) => setSearchByRow((p) => ({ ...p, [index]: e.target.value }))}
                            placeholder="ابحث عن التحليل..."
                            className="w-full mb-2 px-3 py-2 text-sm border border-slate-200 rounded-lg"
                          />
                          <select
                            value={row.lab_test_id}
                            onChange={(e) => setRowValue(index, { lab_test_id: e.target.value })}
                            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg bg-white"
                          >
                            <option value="">اختر التحليل</option>
                            {testsForRow.map((test) => (
                              <option key={test._id} value={test._id}>
                                {(test.name_ar || test.name_en) + ` (${test._id.slice(-6)})`}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            step="any"
                            value={row.target_mean}
                            onChange={(e) => setRowValue(index, { target_mean: e.target.value })}
                            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            step="any"
                            min="0"
                            value={row.target_sd}
                            onChange={(e) => setRowValue(index, { target_sd: e.target.value })}
                            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => removeRow(index)}
                            className="p-2 rounded-lg text-rose-600 hover:bg-rose-50"
                            title="حذف الصف"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-60"
            >
              <Save className="w-4 h-4" />
              {saving ? 'جاري الحفظ...' : editingId ? 'تحديث الكنترول' : 'حفظ الكنترول'}
            </button>
            {editingId && (
              <button
                onClick={resetForm}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50"
              >
                <XCircle className="w-4 h-4" />
                إلغاء التعديل
              </button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-base font-semibold text-slate-800">الكنترولات المسجلة</h3>
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full sm:w-72 px-3 py-2.5 border border-slate-200 rounded-xl"
              placeholder="بحث بالاسم أو رقم التشغيلة"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-slate-200 text-right text-xs text-slate-500">
                  <th className="px-4 py-3">اسم الكنترول</th>
                  <th className="px-4 py-3">Lot</th>
                  <th className="px-4 py-3">المستوى</th>
                  <th className="px-4 py-3">حد الرفض</th>
                  <th className="px-4 py-3">تاريخ الصلاحية</th>
                  <th className="px-4 py-3">عدد التحاليل</th>
                  <th className="px-4 py-3">الحالة</th>
                  <th className="px-4 py-3">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td className="px-4 py-6 text-slate-500" colSpan={7}>
                      جاري التحميل...
                    </td>
                  </tr>
                ) : filteredControls.length === 0 ? (
                  <tr>
                    <td className="px-4 py-6 text-slate-500" colSpan={7}>
                      لا توجد بيانات
                    </td>
                  </tr>
                ) : (
                  filteredControls.map((control) => {
                    const isExpired = control.expiry_date
                      ? new Date(control.expiry_date) < new Date()
                      : false;
                    return (
                      <tr key={control._id} className="border-b border-slate-100 last:border-b-0">
                        <td className="px-4 py-3 font-medium text-slate-800">{control.control_name}</td>
                        <td className="px-4 py-3 text-slate-600">{control.lot_number}</td>
                        <td className="px-4 py-3 text-slate-600">{control.level}</td>
                        <td className="px-4 py-3 text-slate-600">
                          {control.critical_rejection_threshold === '2sd' ? '±2 SD' : '±3 SD'}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {control.expiry_date ? String(control.expiry_date).slice(0, 10) : '-'}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{control.items?.length || 0}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${
                              isExpired
                                ? 'bg-rose-100 text-rose-700'
                                : 'bg-emerald-100 text-emerald-700'
                            }`}
                          >
                            {isExpired ? 'منتهي' : 'نشط'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => onEdit(control)}
                              className="p-2 rounded-lg text-blue-600 hover:bg-blue-50"
                              title="تعديل"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => onDelete(control._id)}
                              className="p-2 rounded-lg text-rose-600 hover:bg-rose-50"
                              title="حذف"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default QCMaterialsSetup;
